import { useState, useCallback, useRef, useEffect } from 'react';
import { publishersService, Title } from '../services/firebaseFunctions';

export type PublisherLinkItem = { titleId: string; order: number };

type NotifyHandlers = {
  onAfterSync: () => void | Promise<void>;
  onNotifySuccess: (message: string) => void;
  onNotifyError: (message: string) => void;
};

export function usePublisherLinkTitlesModal(
  publisherId: string | undefined,
  allTitles: Title[],
  { onAfterSync, onNotifySuccess, onNotifyError }: NotifyHandlers
) {
  const [open, setOpen] = useState(false);
  const [linkItems, setLinkItems] = useState<PublisherLinkItem[]>([]);
  const [saving, setSaving] = useState(false);

  const savingRef = useRef(false);
  useEffect(() => {
    savingRef.current = saving;
  }, [saving]);

  const notifyRef = useRef({ onAfterSync, onNotifySuccess, onNotifyError });
  useEffect(() => {
    notifyRef.current = { onAfterSync, onNotifySuccess, onNotifyError };
  }, [onAfterSync, onNotifySuccess, onNotifyError]);

  const openModal = useCallback((linkedTitles: Title[]) => {
    setLinkItems(
      linkedTitles.length === 0
        ? []
        : linkedTitles.map((t, i) => ({
            titleId: t.id,
            order: i + 1,
          }))
    );
    setOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    if (!savingRef.current) setOpen(false);
  }, []);

  const titlesForSelectRow = useCallback(
    (rowIndex: number) => {
      const taken = new Set(
        linkItems.map((it, i) => (i !== rowIndex && it.titleId ? it.titleId : null)).filter(Boolean) as string[]
      );
      return allTitles.filter((t) => !taken.has(t.id) || linkItems[rowIndex]?.titleId === t.id);
    },
    [allTitles, linkItems]
  );

  const handleAddLinkItem = useCallback(() => {
    setLinkItems((prev) => [...prev, { titleId: '', order: prev.length + 1 }]);
  }, []);

  const handleRemoveLinkItem = useCallback((index: number) => {
    setLinkItems((prev) => prev.filter((_, i) => i !== index).map((item, i) => ({ ...item, order: i + 1 })));
  }, []);

  const handleLinkItemChange = useCallback((index: number, titleId: string) => {
    setLinkItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], titleId };
      return next;
    });
  }, []);

  const handleMoveLinkUp = useCallback((index: number) => {
    if (index === 0) return;
    setLinkItems((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next.map((item, i) => ({ ...item, order: i + 1 }));
    });
  }, []);

  const handleMoveLinkDown = useCallback((index: number) => {
    setLinkItems((prev) => {
      if (index === prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next.map((item, i) => ({ ...item, order: i + 1 }));
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!publisherId) return;
    const desiredIds = Array.from(new Set(linkItems.map((it) => it.titleId).filter(Boolean)));
    const { onAfterSync: after, onNotifySuccess: ok, onNotifyError: err } = notifyRef.current;

    try {
      savingRef.current = true;
      setSaving(true);
      const res = await publishersService.syncPublisherTitles(publisherId, desiredIds);
      if (!res.success) throw new Error(res.error || 'Falha ao sincronizar vínculos');
      ok(res.message || 'Livros atualizados.');
      setOpen(false);
      await after();
    } catch (e) {
      err(e instanceof Error ? e.message : 'Erro ao salvar vínculos');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [publisherId, linkItems]);

  return {
    open,
    saving,
    linkItems,
    openModal,
    closeModal,
    titlesForSelectRow,
    handleAddLinkItem,
    handleRemoveLinkItem,
    handleLinkItemChange,
    handleMoveLinkUp,
    handleMoveLinkDown,
    handleSave,
  };
}
